import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList,
  Calendar,
  Video, 
  Activity,
  Heart,
  Settings,
  HelpCircle,
  LogOut,
  Baby,
  FileText,
  ChevronDown,
  Sparkles,
  UserCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useWaitingMembersCount } from "@/hooks/useWaitingMembersCount";
import { ROUTES } from "@/routes";

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string | "waiting-count";
  children?: NavItem[];
}

const topNavItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: ROUTES.PROVIDER },
  { icon: Calendar, label: "My Schedule", path: ROUTES.PROVIDER_SCHEDULE },
  { icon: Users, label: "My Patients", path: ROUTES.PROVIDER_PATIENTS },
  { icon: Activity, label: "Remote Patient Monitoring", path: ROUTES.PROVIDER_MONITORING },
  { icon: Sparkles, label: "Health Search Insights", path: ROUTES.PROVIDER_HEALTH_SEARCH_ANALYTICS },
  { 
    icon: FileText, label: "Intake", path: ROUTES.PROVIDER_INTAKE,
    children: [
      { icon: FileText, label: "Intake Form", path: ROUTES.PROVIDER_INTAKE },
      { icon: Baby, label: "Prenatal Intake", path: ROUTES.PROVIDER_PRENATAL_INTAKE },
    ],
  },
];

const bottomGroupNavItems: NavItem[] = [
  { icon: Video, label: "Telehealth", path: ROUTES.PROVIDER_TELEHEALTH, badge: "waiting-count" },
  { 
    icon: ClipboardList, label: "Case Management", path: ROUTES.PROVIDER_CASES,
    children: [
      { icon: Heart, label: "Care Plans", path: ROUTES.PROVIDER_CARE_PLANS },
      { icon: Baby, label: "PNCC Cases", path: ROUTES.PROVIDER_PNCC },
    ],
  },
];

const bottomNavItems: NavItem[] = [
  { icon: UserCircle, label: "My Profile", path: ROUTES.PROVIDER_PROFILE },
  { icon: Settings, label: "Settings", path: ROUTES.PROVIDER_SETTINGS },
  { icon: HelpCircle, label: "Help & Support", path: ROUTES.PROVIDER_HELP },
];

export function ProviderSidebar() {
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const waitingCount = useWaitingMembersCount();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const isActive = (path: string) => {
    if (path === ROUTES.PROVIDER) {
      return location.pathname === ROUTES.PROVIDER;
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some((child) => isActive(child.path));
    }
    return isActive(item.path);
  };

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isExpanded = (item: NavItem) => {
    return expandedGroups[item.label] ?? isGroupActive(item);
  };

  const getBadgeValue = (badge?: string) => {
    if (badge === "waiting-count") {
      return waitingCount > 0 ? waitingCount.toString() : undefined;
    }
    return badge;
  };

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      const expanded = isExpanded(item);
      const groupActive = isGroupActive(item);
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleGroup(item.label)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
              groupActive
                ? "bg-sidebar-primary/10 text-sidebar-primary-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <item.icon className="h-5 w-5" />
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", expanded && "rotate-180")} />
          </button>
          {expanded && (
            <div className="ml-4 mt-1 space-y-0.5 border-l border-sidebar-border pl-3">
              {item.children.map((child) => (
                <button
                  key={child.label}
                  onClick={() => navigate(child.path)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                    isActive(child.path)
                      ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                >
                  <child.icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{child.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={item.label}
        onClick={() => navigate(item.path)}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
          isActive(item.path)
            ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <item.icon className="h-5 w-5" />
        <span className="flex-1 text-left">{item.label}</span>
        {getBadgeValue(item.badge) && (
          <span className={cn(
            "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold",
            item.badge === "waiting-count" 
              ? "bg-destructive text-destructive-foreground animate-pulse" 
              : "bg-warning text-warning-foreground"
          )}>
            {getBadgeValue(item.badge)}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-sidebar-border bg-sidebar">
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b border-sidebar-border px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg gradient-primary">
            <Activity className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-lg font-bold text-sidebar-foreground">Vivien Health</h1>
            <p className="text-xs text-muted-foreground">Provider Portal</p>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Main Menu
          </p>
          {topNavItems.map(renderNavItem)}

          <div className="my-3 border-t border-sidebar-border" />

          {bottomGroupNavItems.map(renderNavItem)}
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-sidebar-border px-3 py-4">
          {bottomNavItems.map((item) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive(item.path)
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </button>
          ))}
          <button 
            onClick={signOut}
            className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <LogOut className="h-5 w-5" />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
